import "./index.scss";

import { memo, useCallback, type ReactNode } from "react";
import { useIntl, FormattedMessage, defineMessages } from "react-intl";

import Button from "common/core/button";
import { captureException } from "util/exception";
import { userFullName } from "util/user";
import { pushNotification } from "common/core/notification_center/actions";
import { NOTIFICATION_SUBTYPES } from "constants/notifications";
import { AnnotationDesignationType } from "graphql_globals";

export const BATCH_DESIGNATION_TYPES = Object.freeze(
  new Set([
    AnnotationDesignationType.SIGNATURE,
    AnnotationDesignationType.INITIALS,
    AnnotationDesignationType.DATE_SIGNED,
    AnnotationDesignationType.DAY_SIGNED,
    AnnotationDesignationType.MONTH_SIGNED,
    AnnotationDesignationType.YEAR_SIGNED,
  ]),
);

const messages = defineMessages({
  documentCounter: {
    id: "839ccad7-9627-406f-a9bd-384722dbc77e",
    defaultMessage: "Document {currentIndex} of {totalCount}",
  },
  successfulFulfillAll: {
    id: "87b0b1d7-6280-4ffd-8c11-e39392b4d38f",
    defaultMessage: "Signatures have been applied for {signerFullName}",
  },
  partiallyCompleteFulfillAll: {
    id: "4789fba6-b611-4b4d-89a2-b716db59595f",
    defaultMessage: "{unfulfilledCount} of {signerFullName}'s signatures could not be applied.",
  },
  errorFulfillAll: {
    id: "dd133caf-4078-4e27-920b-f98c99e18b5c",
    defaultMessage: "Something went wrong trying to apply {signerFullName}'s signatures.",
  },
});

type Props = {
  user?: {
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
  } | null;
  onClick: () => Promise<{
    error?: Error;
    cancelled: boolean;
    value: number;
  }>;
  submitting: boolean;
  compact: boolean;
};

function AgreementText({ signerFullName }: { signerFullName: string }) {
  return (
    <FormattedMessage
      id="48abd4ef-36e5-4ecf-bd01-53d1e2e3397c"
      defaultMessage={
        'By clicking "Sign now", I, <b>{signerFullName}</b>, acknowledged that I have reviewed and ' +
        "approve of this document and agree to the application of my signature(s), " +
        "initial(s), and annotations where labels are present."
      }
      tagName="div"
      values={{
        signerFullName,
        b: (txt: ReactNode) => <strong>{txt}</strong>,
      }}
    />
  );
}

function BatchSign(props: Required<Props>) {
  const { user, submitting, onClick, compact } = props;
  const intl = useIntl();

  const signerFullName = userFullName(user);

  const handleClick = useCallback(() => {
    onClick()
      .then(({ error, cancelled, value }) => {
        if (error) {
          pushNotification({
            message: intl.formatMessage(messages.errorFulfillAll, { signerFullName }),
            subtype: NOTIFICATION_SUBTYPES.ERROR,
          });
          return;
        } else if (cancelled) {
          return;
        } else if (value > 0) {
          pushNotification({
            message: intl.formatMessage(messages.partiallyCompleteFulfillAll, {
              signerFullName,
              unfulfilledCount: value,
            }),
            subtype: NOTIFICATION_SUBTYPES.WARNING,
          });
          return;
        }

        pushNotification({
          message: intl.formatMessage(messages.successfulFulfillAll, { signerFullName }),
          subtype: NOTIFICATION_SUBTYPES.SUCCESS,
        });
      })
      .catch((error) => {
        captureException(error, {
          user,
        });
      });
  }, [user, onClick]);

  return (
    <div className={`BatchSign${compact ? "__compact" : ""}`}>
      <div className={`BatchSign--body${compact ? "__compact" : ""}`}>
        {!compact && <AgreementText signerFullName={signerFullName} />}
      </div>
      <Button
        className="BatchSign--button"
        buttonColor="action"
        variant="primary"
        onClick={handleClick}
        isLoading={submitting}
        automationId="batch-sign-button"
      >
        <FormattedMessage id="6deecab4-75db-4a07-b63d-10fb000a4bfc" defaultMessage="Sign now" />
      </Button>
    </div>
  );
}

function WrappedBatchSign(props: Props & { hidden: boolean }) {
  const { user, hidden, submitting, onClick, compact } = props;

  if (!user || hidden) {
    return (
      <div className="BatchSign BatchSign__hidden">
        <div style={{ height: "64px" }} />
      </div>
    );
  }

  return <BatchSign user={user} submitting={submitting} onClick={onClick} compact={compact} />;
}

export default memo(WrappedBatchSign);
